import { Button, Flex, Form, Grid, Input, ModalManagerContext } from '@pega/cosmos-react-core';
import { ModalMethods } from '@pega/cosmos-react-core/lib/components/Modal/Modal.types';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useLocalTranslation from '../../../../hooks/useLocalTranslation';
import { saveChatPayload } from '../../../../interfaces/chat';
import { appState, confirmationAlertProps, SDKComponentProps } from '../../../../interfaces/common';
import { setChatInfo, updateBannerInfo, updateLoaderDetails } from '../../../../redux/actions';
import { ConfirmationAlert } from '../../../Utilities/CustomModal';
import { addNewChatAccount, deleteAccount, updateChatAccount } from '../ChatApis';
import DisplayToggles from './DisplayToggles';

const GeneralTabSDK: React.FC<SDKComponentProps> = React.memo((props: SDKComponentProps) => {
  const { platform } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const translate = useLocalTranslation();
  const { register, handleSubmit, errors, reset } = useForm({ mode: 'all' });
  const { create } = useContext(ModalManagerContext);
  const modalMethods = useRef<ModalMethods<Partial<confirmationAlertProps>>>();
  const {
    encryptionEnableFlag,
    chatInfo: { accountName, connectionId },
    sessionId,
  } = useSelector((state: appState) => state);

  useEffect(() => {
    const formValues = {
      accountName,
    };
    reset(formValues);
  }, [reset, accountName]);

  const deleteChatChannel = useCallback(async () => {
    dispatch(updateLoaderDetails(true));
    let response = await deleteAccount(connectionId, sessionId);
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      navigate('/');
      dispatch(updateBannerInfo(`${platform ? translate(platform) : ''} ${translate('account_deleted')}`, 'success'));
    } else if (response?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    } else {
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    }
  }, [connectionId, dispatch, navigate, platform, sessionId, translate]);

  const deleteConfirmation = () => {
    modalMethods.current = create(
      ConfirmationAlert,
      { pageName: accountName, deleteChannel: deleteChatChannel },
      { alert: true },
    );
  };

  const actions = (
    <Flex
      container={{
        alignItems: 'start',
      }}
    >
      <Button name="Save" type="submit" variant="primary">
        {translate('save')}
      </Button>
    </Flex>
  );

  const createChatChannel = async (formData: saveChatPayload) => {
    let json,
      operation = '';
    const data = { ...formData, sdkPlatform: platform, encryptionEnabled: encryptionEnableFlag };
    if (encryptionEnableFlag) {
      data['attachmentIcon'] = false;
      data['displayDownloadTranscript'] = false;
    }
    if (data.accountName.trim() !== accountName) {
      dispatch(updateLoaderDetails(true));
      if (connectionId) {
        json = await updateChatAccount(sessionId, connectionId, data);
        operation = 'update';
      } else {
        json = await addNewChatAccount(sessionId, data);
        operation = 'create';
      }
      dispatch(updateLoaderDetails(false));
      if (json?.status === 200) {
        dispatch(
          updateBannerInfo(`${platform ? translate(platform) : ''} account successfully ${operation}d`, 'success'),
        );
        dispatch(setChatInfo(json?.data));
      } else if (json?.status === 403) {
        navigate('/');
        dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
      } else {
        dispatch(updateBannerInfo(`Failed to ${operation} ${platform ? translate(platform) : ''} account`, 'urgent'));
      }
    }
  };
  return (
    <Flex
      container={{
        direction: 'column',
        itemGap: 3,
      }}
    >
      <Form actions={actions} onSubmit={handleSubmit(createChatChannel)}>
        <Flex container={{ direction: 'column', itemGap: 3 }} item={{ grow: 1 }}>
          <Grid
            container={{
              cols: 'repeat(2, 1fr)',
              gap: 2,
            }}
          >
            <Input
              label={translate('name')}
              type="text"
              name="accountName"
              required={true}
              autoComplete="off"
              info={errors.accountName?.message}
              status={errors.accountName?.message ? 'error' : undefined}
              ref={register({ required: translate('name_empty') })}
            />
          </Grid>
        </Flex>
      </Form>
      {connectionId && <DisplayToggles showSDKConfigs={true} />}
      {connectionId && (
        <Flex>
          <Button variant="secondary" onClick={deleteConfirmation}>
            {translate('delete_connection_button')}
          </Button>
        </Flex>
      )}
    </Flex>
  );
});

export default GeneralTabSDK;
